import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, ChevronUp, CircleUserRound, X } from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Combobox } from '@/components';
import { useERSContext, useMapContext } from '@/context';
import { constants } from '@/lib/constants';

const ASSIGNEE_STATUS = [
  { value: 'not_started', label: 'Not Started' },
  { value: 'on_the_way', label: 'On the way' },
  { value: 'reached_site', label: 'Reach site' },
  { value: 'enroute', label: 'Enroute' },
  { value: 'done', label: 'Done' },
];

// Helper to get presence status indicator color
const getPresenceColor = presence => {
  switch (presence) {
    case 'disconnected':
      return 'bg-error-strong';
    case 'offline':
      return 'bg-disabled';
    case 'online':
    default:
      return 'bg-success-strong';
  }
};

// Helper to get badge variant based on presence
const getBadgeVariant = presence => {
  switch (presence) {
    case 'online':
      return 'SoftGreen';
    case 'offline':
      return 'secondary';
    case 'disconnected':
      return 'destructive';
    default:
      return 'secondary';
  }
};

// Format timestamp to readable time
const formatTime = timestamp => {
  if (!timestamp) return '';
  const date = typeof timestamp === 'number' ? new Date(timestamp * 1000) : new Date(timestamp);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

export const ResponderDetail = ({
  assignee,
  statusList = ASSIGNEE_STATUS.map(status => ({
    id: status.value,
    display_name: status.label,
  })),
  mode = 'view', // 'view' or 'edit'
  onClose,
  onResponseStatusChange,
  onCurrentStatusChange,
}) => {
  const { t } = useTranslation();
  const [expandedStatus, setExpandedStatus] = useState(false);
  const [responseStatus, setResponseStatus] = useState(assignee?.response_status || 'pending');
  const [currentStatus, setCurrentStatus] = useState('');
  const { users } = useERSContext();
  const { focusResponder } = useMapContext();

  const filterPresenceFromUsers = idOrUser => {
    const statusColors = constants.RESPONDERS.LIST.STATUS_COLORS;
    const presenceStatus = constants.RESPONDERS.LIST.PRESENCE_STATUS;
    let user;
    if (idOrUser && idOrUser.presence) {
      user = idOrUser;
    } else {
      user = users.find(u => u.id.split('@')[0] === idOrUser);
    }
    const presence = user?.presence;
    switch (presence) {
      case presenceStatus.DISCONNECTED:
        return statusColors.DISCONNECTED;

      case presenceStatus.OFFLINE:
        return statusColors.OFFLINE;

      case presenceStatus.ONLINE:
        return statusColors.ONLINE;

      default:
        return statusColors.DISCONNECTED;
    }
  };

  const displayName = useMemo(
    () => assignee?.display_name || assignee?.displayName || assignee?.id || 'Responder',
    [assignee?.display_name, assignee?.displayName, assignee?.id]
  );

  // Get status label from status list
  const getStatusLabel = useCallback(
    statusId => {
      const status = statusList.find(s => s.id === statusId);
      return status?.display_name;
      // return status?.display_name || statusId;
    },
    [statusList]
  );
  const normalize = str => str?.toLowerCase().replaceAll(' ', '_');

  // Memoized status records
  const statusRecords = useMemo(() => assignee?.status_records || [], [assignee?.status_records]);
  const hasRecords = statusRecords.length > 0;
  const visibleRecords = expandedStatus ? statusRecords : statusRecords.slice(-1);

  // Prepare combobox data for status list
  const statusOptions = useMemo(
    () =>
      statusList.map(status => ({
        value: status.id,
        label: t('ASSIGNEE_STATUS', { returnObjects: true }).find(s => s.value === normalize(status?.display_name))
          ?.label,
      })),
    [statusList]
  );

  // Get selected data for comboboxes
  const selectedResponseStatus = useMemo(
    () => t('RESPONSE_STATUS', { returnObjects: true }).find(opt => opt.value === responseStatus), // RESPONSE_STATUS
    [responseStatus]
  );

  const selectedCurrentStatus = useMemo(
    () => statusOptions.find(opt => opt.value === currentStatus),
    [statusOptions, currentStatus]
  );

  // Event handlers with useCallback
  const handleResponseStatusChange = useCallback(
    selectedItem => {
      if (selectedItem) {
        setResponseStatus(selectedItem.value);
        onResponseStatusChange?.(assignee?.id, selectedItem.value);

        if (selectedItem.value !== 'accepted') {
          setCurrentStatus('');
        }
      }
    },
    [assignee?.id, onResponseStatusChange]
  );

  const handleCurrentStatusChange = useCallback(
    selectedItem => {
      if (selectedItem) {
        setCurrentStatus(selectedItem.value);
        onCurrentStatusChange?.(assignee?.id, selectedItem.value);
      }
    },
    [assignee?.id, onCurrentStatusChange]
  );

  const toggleExpanded = useCallback(e => {
    e.stopPropagation();
    setExpandedStatus(prev => !prev);
  }, []);

  const handleResponderClick = id => {
    const userObj = users?.find(u => u?.id?.split('@')[0] === id);
    focusResponder(userObj);
  };

  // View Mode - Compact display
  // View Mode - Now includes status history
  if (mode === 'view') {
    const presenceColor = assignee?.presence
      ? filterPresenceFromUsers(assignee)
      : filterPresenceFromUsers(assignee?.id);

    return (
      <div className="flex flex-col gap-2 w-full">
        {/* Responder Header Pill */}
        <span
          onClick={() => handleResponderClick(assignee?.id)}
          className="flex flex-row items-center justify-between gap-2 px-2 py-1.5 rounded-lg shadow-sm bg border border-input text text-sm font-normal cursor-pointer"
        >
          <div className="flex flex-row gap-2 items-center">
            <div className="relative">
              <CircleUserRound size={24} absoluteStrokeWidth className="text-icon" />
              <span
                className={`absolute bottom-0 right-0 block h-2 w-2 rounded-full border-2 border-bg ${presenceColor}`}
              />
            </div>
            <span className="font-medium">{displayName}</span>
          </div>
        </span>

        {/* Status History (Visible in View Mode) */}
        {hasRecords && (
          <div className="pl-8 pr-1">
            <div className="space-y-1.5">
              {visibleRecords.map((record, idx) => {
                const statusLabel = getStatusLabel(record.status);
                const timeStr = formatTime(record.timestamp);
                const isLast = idx === visibleRecords.length - 1;
                const showToggle = isLast && statusRecords.length > 1;

                return (
                  <div key={`${record.status}-${record.timestamp}-${idx}`} className="flex items-center gap-1">
                    <div className="flex items-center gap-2 bg-secondary/50 border border-input/50 px-2 py-1 rounded-md flex-shrink-0">
                      <span className="text-[11px] font-medium text">{statusLabel}</span>
                      <span className="text-[10px] text-muted">{timeStr}</span>
                    </div>

                    {showToggle && (
                      <button
                        className="flex items-center gap-0.5 text-interactive text-[11px] font-medium hover:underline ml-1"
                        onClick={toggleExpanded}
                        type="button"
                      >
                        {expandedStatus ? (
                          <>
                            <ChevronUp size={12} />
                            <p className="whitespace-nowrap">{t('INCIDENTS_PANEL.SHOW_LESS')}</p>
                          </>
                        ) : (
                          <>
                            <ChevronDown size={14} />
                            <p className="whitespace-nowrap">
                              {t('INCIDENTS_PANEL.SHOW')} {statusRecords.length - 1} {t('INCIDENTS_PANEL.MORE')}
                            </p>
                          </>
                        )}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Edit Mode - Full detailed view
  return (
    <div className="w-full max-w-3xl bg border border-input rounded-xl shadow-lg overflow-visible">
      {/* Header */}
      <div className="bg-subtle px-6 py-4 border-b border-input">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="text-icon-muted hover:text-icon transition-colors"
              aria-label="Close"
              type="button"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <div className="relative">
                <CircleUserRound size={24} absoluteStrokeWidth className="text-icon" />
                <span
                  className={`absolute bottom-0 right-0 block h-2 w-2 rounded-full border-2 border-bg ${filterPresenceFromUsers(
                    assignee?.id
                  )}`}
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text min-w-20">{displayName}</span>
                <div className="bg-secondary text px-2.5 py-0.5 text-xs font-medium rounded">
                  {t('RESPONSE_STATUS', { returnObjects: true }).find(status => status.value === responseStatus)
                    ?.label || responseStatus}
                </div>
              </div>
            </div>
          </div>

          <div className="w-44">
            <Combobox
              data={t('RESPONSE_STATUS', { returnObjects: true })}
              selectedData={selectedResponseStatus}
              onChange={handleResponseStatusChange}
              placeholder={t('PLACEHOLDER.SELECT_STATUS')}
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-5">
        {/* Status Records Timeline */}
        {hasRecords && (
          <div className="pb-6">
            <h3 className="text-sm font-semibold text pb-3">{t('INCIDENTS_PANEL.STATUS_HISTORY')}</h3>
            <div className="space-y-2.5">
              {visibleRecords.map((record, idx) => {
                const statusLabel = getStatusLabel(record.status);
                const timeStr = formatTime(record.timestamp);
                const isLast = idx === visibleRecords.length - 1;
                const showToggle = isLast && statusRecords.length > 1;

                return (
                  <div key={`${record.status}-${record.timestamp}-${idx}`} className="flex items-center gap-1 group">
                    <div className="flex items-center gap-2 bg-secondary border border-input px-3 py-1.5 rounded-lg flex-shrink-0">
                      <span className="text-sm font-medium text">{statusLabel}</span>
                      <span className="text-xs text-muted">{timeStr}</span>
                    </div>
                    {showToggle && (
                      <button
                        className="flex items-center gap-1 text-interactive text-xs font-medium hover:text-interactive-strong hover:underline transition-colors"
                        onClick={toggleExpanded}
                        type="button"
                        aria-expanded={expandedStatus}
                        aria-label={
                          expandedStatus
                            ? 'Show less status history'
                            : `Show ${statusRecords.length - 1} more status records`
                        }
                      >
                        {expandedStatus ? (
                          <>
                            <ChevronUp size={14} />
                            <p className="whitespace-nowrap">{t('INCIDENTS_PANEL.SHOW_LESS')}</p>
                          </>
                        ) : (
                          <>
                            <ChevronDown size={14} />
                            <p className="whitespace-nowrap">
                              {t('INCIDENTS_PANEL.SHOW')} {statusRecords.length - 1} {t('INCIDENTS_PANEL.MORE')}
                            </p>
                          </>
                        )}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Current Status Selector */}
        <AnimatePresence>
          {responseStatus === 'accepted' && (
            <motion.div
              key="status-selector"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="bg-subtle border border-input rounded-lg p-4"
            >
              <div className="flex items-center gap-4">
                <label htmlFor="status-selector" className="text-sm font-semibold text whitespace-nowrap">
                  {t('UPDATES_PANEL.UPDATE_STATUS')}
                </label>
                <div className="flex-1">
                  <Combobox
                    id="status-selector"
                    data={statusOptions}
                    selectedData={selectedCurrentStatus}
                    onChange={handleCurrentStatusChange}
                    placeholder={t('PLACEHOLDER.CHOOSE_STATUS')}
                  />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
